import { Component, Input, OnInit } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';
import { MenuItem } from 'primeng/api';
import * as XLSX from 'xlsx';
import { GlobalService } from 'src/app/api/global.service';
import { ApiService } from 'src/app/api/api.service';
import { CompaniesContactsSelectPage as ContactsSelectPage } from 'src/app/modals/companies-contacts-select/companies-contacts-select.page';
import { CreatePage as ClientCreatePage } from 'src/app/clients/create/create.page';
import { SelectPage as TeamMembersSelectPage } from 'src/app/team-members/select/select.page';
import { SelectComponent as TagsSelectPage } from 'src/app/components/tags/select/select.component';
import { ManagerComponent as GroupsManagerPage } from 'src/app/components/groups/manager/manager.component';
import { parseNamesFromDisplayName, parseNameFromDisplayName } from 'src/app/services/name-parser.util';

export interface FilePreviewData {
  rowCount: number;
  columnCount: number;
  sheetName: string;
  headers: string[];
  sampleData: any[];
}

export interface FieldMapping {
  excelColumn: string;
  companyField: string;
  sampleValues: string[];
  columnIndex: number;
}

export interface CompanyField {
  key: string;
  label: string;
  required: boolean;
}

@Component({
  selector: 'app-import',
  templateUrl: './import.page.html',
  styleUrls: ['./import.page.scss'],
})
export class ImportPage implements OnInit {
   team_member_roles = [];

  // When set (e.g. by the Client Track import wizard), skips the manual upload step entirely -
  // ProcessFile() runs against this instead of a user-picked file, landing straight on "Remap
  // Fields" once parsing succeeds, same as if the file had been dropped in by hand.
  @Input() preloadedFile: File | null = null;

  // Column names (matching rawHeaders) to leave out of the primary field-mapping list - they still
  // stay in rawHeaders so the Custom Field builder below can pick them, since we don't know what a
  // user might still want as a custom field.
  @Input() excludeFromMapping: string[] = [];

  // Pre-resolved groups/clients to attach to each company row, index-aligned with parsedData (e.g.
  // by the Client Track import wizard, matching legacy CompanyType -> an existing group, and legacy
  // linked contacts -> existing Fusion clients). Merged in during ProcessMappings() alongside whatever
  // the column mapping/default-contact matching produces.
  @Input() presetGroupsByRow: any[][] = [];
  @Input() presetClientsByRow: any[][] = [];
  // Custom fields to set up automatically once the file is read (e.g. the Client Track import's company
  // Email). Only added when that column is in the file and isn't already used; the user can still remove them.
  @Input() presetCustomFields: { name: string; sourceColumn: string; type?: string }[] = [];

  // Available company fields for mapping
  companyFields: CompanyField[] = [
    { key: '', label: '-- Select a field --', required: false },
    { key: 'name', label: 'Company Name', required: true },
    { key: 'year_end_date', label: 'Year End Date (any format - e.g. Dec 31, 12/31, 31/12, 12-31-26)', required: false },
    { key: 'year_end_month', label: 'Year End Month (01-12)', required: false },
    { key: 'year_end_day', label: 'Year End Day (01-31)', required: false },
    { key: 'all_team_members', label: 'Visible to All Team Members (Yes/No)', required: false },
    { key: 'is_a_client', label: 'Your Client (Yes/No)', required: false },
    { key: 'phone_number', label: 'Phone Number', required: false },
    { key: 'fax', label: 'Fax', required: false },
    { key: 'website', label: 'Website', required: false },
    { key: 'str_address_1', label: 'Street Address', required: false },
    { key: 'str_address_2', label: 'Street Address Line 2', required: false },
    { key: 'city', label: 'City', required: false },
    { key: 'province', label: 'Province/State', required: false },
    { key: 'postal_code', label: 'Postal/Zip Code', required: false },
    { key: 'country', label: 'Country', required: false },
    { key: 'corp_name', label: 'Corporate/Legal Name', required: false },
    { key: 'custom_id', label: 'Custom ID', required: false },
    { key: 'business_number', label: 'Business Number', required: false },
    { key: 'industry', label: 'Industry', required: false },
    { key: 'type', label: 'Company Type', required: false },
    { key: 'description', label: 'Notes/Description', required: false },
    { key: 'default_contact', label: 'Default Contact Display Name', required: false },
    // Every other field a contact can have (see clients/import's contactFields) - all only ever used
    // alongside default_contact above, see ProcessMappings(). Mapped without a Default Contact
    // Display Name column, they're simply discarded. First/Last/Middle Name here override the name
    // ProcessMappings() would otherwise parse out of the Display Name automatically.
    { key: 'default_contact_first_name', label: 'Default Contact First Name', required: false },
    { key: 'default_contact_last_name', label: 'Default Contact Last Name', required: false },
    { key: 'default_contact_middle_name', label: 'Default Contact Middle Name', required: false },
    { key: 'default_contact_email', label: 'Default Contact Email', required: false },
    { key: 'default_contact_phone_number', label: 'Default Contact Phone Number', required: false },
    { key: 'default_contact_cell_phone', label: 'Default Contact Cell Phone', required: false },
    { key: 'default_contact_alt_phone', label: 'Default Contact Alt Phone', required: false },
    { key: 'default_contact_fax', label: 'Default Contact Fax', required: false },
    { key: 'default_contact_address', label: 'Default Contact Address', required: false },
    { key: 'default_contact_city', label: 'Default Contact City', required: false },
    { key: 'default_contact_province', label: 'Default Contact Province', required: false },
    { key: 'default_contact_country', label: 'Default Contact Country', required: false },
    { key: 'default_contact_postal_code', label: 'Default Contact Postal Code', required: false },
    { key: 'default_contact_website', label: 'Default Contact Website', required: false },
    { key: 'default_contact_custom_id', label: 'Default Contact Custom ID', required: false },
    { key: 'default_contact_gender', label: 'Default Contact Gender', required: false },
    { key: 'default_contact_birthday', label: 'Default Contact Birthday', required: false },
    { key: 'default_contact_anniversary', label: 'Default Contact Anniversary', required: false },
    { key: 'default_contact_sin', label: 'Default Contact SIN/SSN', required: false },
    { key: 'default_contact_spoken_language', label: 'Default Contact Spoken Language', required: false },
    { key: 'default_contact_custom_data', label: 'Default Contact Notes/Custom Data', required: false },
  ];

  activeIndex = 0;
  items: MenuItem[];

  // File upload state
  uploadedFile: File | null = null;
  isDragOver = false;
  isUploading = false;
  uploadProgress = 0;
  uploadError: string | null = null;
  filePreviewData: FilePreviewData | null = null;

  // Parsed data
  parsedData: any[] = [];
  rawHeaders: string[] = [];

  // Field mapping state
  fieldMappings: FieldMapping[] = [];

  // Preview/Review state
  mappedCompanies: any[] = [];
  previewCompanies: any[] = [];
  validationErrors: string[] = [];

  // Validation tracking
  hasValidationErrors = false;
  validCompaniesCount = 0;
  invalidCompaniesCount = 0;
  isValidated = false;
  isValidating = false;

  // Pagination
  currentPage = 1;
  pageSize = 25;

  // Duplicate name tracking
  duplicateNames: { name: string; indices: number[] }[] = [];
  hasDuplicateNames = false;

  // View mode: 'compact' for table view, 'comfort' for card view
  viewMode: 'compact' | 'comfort' = 'compact';

  // When true (and only meaningful once isValidated), the table/cards show only companies with
  // validation errors - lets you find the handful of bad rows in an otherwise huge import instead of
  // paging through everything.
  showErrorsOnly = false;

  // Data quality issues
  dataQualityIssues: string[] = [];
  hasDataQualityIssues = false;

  // Contact assignment tracking
  companiesWithoutContacts = 0;
  companiesWithoutDefaultContact = 0;
  // Subset of companiesWithoutDefaultContact whose default contact name matched more than one
  // existing contact (see _ambiguousDefaultContactMatches on the row) - called out separately so
  // the user knows these need a manual pick, not that nothing was found at all.
  companiesWithAmbiguousDefaultContact = 0;

  // All available clients fetched for auto-matching
  all_available_clients: any[] = [];

  // All client roles fetched for auto-assigning the highest role
  all_available_roles: any[] = [];

  // Existing company name match warnings (not blocking)
  existingCompanyNames: string[] = [];
  existingNameMatches: { rowNumber: number; companyName: string }[] = [];
  hasExistingNameMatches = false;

  // Group assignment
  selectedGroups: any[] = [];

  // Tag assignment
  selectedTags: any[] = [];

  // Bulk year end edit
  bulkYearEndMonth: string = '';
  bulkYearEndDay: string = '';
  daysInMonth: string[] = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));

  // Custom field definitions for import mapping
  // type is the custom field's type (see custom-fields/manage) - 'text' unless set, e.g. by presetCustomFields
  customFieldDefinitions: { name: string; sourceColumn: string; type?: string }[] = [];

  // Default Contact display name builder - mirrors clients/import's identical builder (see
  // IsDisplayNameMapped/AddDisplayNamePart/etc below and ProcessMappings' resolution order:
  // mapped Default Contact Display Name column -> this builder, if configured -> Default Contact
  // First + Last Name columns.
  displayNameParts: string[] = [];
  displayNameSeparator: string = ' ';
  displayNameColumnToAdd: string = '';

  constructor(private modalController: ModalController, public globalService: GlobalService, private apiService: ApiService, private alertController: AlertController) { }

  ngOnInit() {
    this.GetTeamRoles();
    this.items = [
      { label: 'Upload Excel Sheet' },
      { label: 'Remap Fields' },
      { label: 'Validate' }
    ];
    if (this.preloadedFile) this.ProcessFile(this.preloadedFile);
  }

  async GetTeamRoles() {
    let data: any = await this.apiService.get('firms/team_members/roles');
    if (data.success) {
      this.team_member_roles = data.data;
    }
  }

  async presentSelectGroupsModal() {
    const modal = await this.modalController.create({
      component: GroupsManagerPage,
      cssClass: 'medium-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'companies',
        selection_mode: true,
        max_selection: 999,
        selected_groups: [...this.selectedGroups],
        modal_title: 'Select Groups'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        this.selectedGroups = data.data;
      }
    });
    return await modal.present();
  }

  async presentSelectTagsModal() {
    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'companies',
        selected_tags: this.selectedTags
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.selectedTags = data.data;
      }
    });
    return await modal.present();
  }

  async Close() {
    await this.modalController.dismiss();
  }

  async GoToStep(step: number) {
    this.activeIndex = step;
  }

  async NextStep() {
    if (this.activeIndex === 1) {
      try {
        const [clientsResponse, rolesResponse]: any[] = await Promise.all([
          this.apiService.get('clients'),
          this.apiService.GetRoles()
        ]);
        this.all_available_clients = (clientsResponse.success && clientsResponse.data) ? clientsResponse.data : [];
        this.all_available_roles = (rolesResponse.success && rolesResponse.data) ? rolesResponse.data : [];
      } catch (e) {
        this.all_available_clients = [];
        this.all_available_roles = [];
      }
      console.log('All available clients:', this.all_available_clients);
      this.ProcessMappings();
    }
    if (this.activeIndex < this.items.length - 1) {
      this.activeIndex++;
    }
  }

  PreviousStep() {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    }
  }

  // Drag and drop handlers
  OnDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  OnDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  OnDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.ProcessFile(files[0]);
    }
  }

  OnFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.ProcessFile(input.files[0]);
    }
  }

  ProcessFile(file: File): void {
    // Reset state
    this.uploadError = null;
    this.filePreviewData = null;
    this.parsedData = [];
    this.rawHeaders = [];
    this.fieldMappings = [];
    this.dataQualityIssues = [];
    this.hasDataQualityIssues = false;
    this.displayNameParts = [];
    this.displayNameSeparator = ' ';
    this.displayNameColumnToAdd = '';

    // Validate file type by extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    const validExtensions = ['xlsx', 'xls', 'csv'];

    if (!validExtensions.includes(extension || '')) {
      this.uploadError = 'Invalid file type. Please upload an Excel (.xlsx, .xls) or CSV file.';
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = 'File is too large. Maximum file size is 10MB.';
      return;
    }

    this.uploadedFile = file;
    this.isUploading = true;
    this.uploadProgress = 0;

    // Read and parse the file
    const reader = new FileReader();

    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        this.uploadProgress = Math.round((event.loaded / event.total) * 50);
      }
    };

    reader.onload = (event) => {
      try {
        this.uploadProgress = 60;
        const data = event.target?.result;
        // cellDates: true - without this, a column actually formatted as a Date in Excel (very
        // common for something literally called "Year End Date") comes through as a raw serial
        // number (e.g. 44561) instead of a real date, which ParseYearEndDate has no way to make
        // sense of as a month/day string. This makes XLSX hand back a JS Date object instead, which
        // ProcessMappings already converts to an ISO 'YYYY-MM-DD' string before parsing.
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });

        this.uploadProgress = 80;

        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON with header row
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          this.uploadError = 'The file appears to be empty.';
          this.isUploading = false;
          this.uploadedFile = null;
          return;
        }

        // Extract headers (first row)
        // Array.from (not .map) - sheet_to_json returns sparse arrays, so blank header cells are
        // holes that .map would skip and leave undefined.
        this.rawHeaders = Array.from(jsonData[0] as any[], (h, index) =>
          h ? String(h).trim() : `Column ${index + 1}`
        );

        // Extract data rows (skip header)
        this.parsedData = jsonData.slice(1).filter((row: any[]) =>
          row && row.some(cell => cell !== null && cell !== undefined && cell !== '')
        );

        this.uploadProgress = 90;

        // Validate data quality
        this.ValidateDataQuality();

        // If there are critical issues, stop processing
        if (this.hasDataQualityIssues) {
          this.uploadProgress = 100;
          this.isUploading = false;
          return;
        }

        this.uploadProgress = 100;

        // Set preview data
        this.filePreviewData = {
          rowCount: this.parsedData.length,
          columnCount: this.rawHeaders.length,
          sheetName: firstSheetName,
          headers: this.rawHeaders,
          sampleData: this.parsedData.slice(0, 5)
        };

        // Initialize field mappings with auto-detection
        this.InitializeFieldMappings();

        this.isUploading = false;

        // A file provided programmatically (e.g. the Client Track import wizard) means the
        // "pick a file" step already happened for the user - skip straight to reviewing the mapping.
        if (this.preloadedFile) this.activeIndex = 1;
      } catch (error) {
        console.error('Error parsing file:', error);
        this.uploadError = 'Failed to parse the file. Please make sure it is a valid Excel or CSV file.';
        this.isUploading = false;
        this.uploadedFile = null;
      }
    };

    reader.onerror = () => {
      this.uploadError = 'Failed to read the file. Please try again.';
      this.isUploading = false;
      this.uploadedFile = null;
    };

    reader.readAsArrayBuffer(file);
  }

  ValidateDataQuality(): void {
    this.dataQualityIssues = [];

    if (this.parsedData.length === 0) {
      this.dataQualityIssues.push('No data rows found. The file only contains a header row or is empty.');
    }

    // Every check below is a heuristic for a messy hand-made spreadsheet - skipped for a preloaded file
    // (e.g. the Client Track import wizard), which is a known-good backend export. It can legitimately
    // have a wide legacy schema and very few rows (anyone already imported is dropped).
    if (this.preloadedFile) {
      this.hasDataQualityIssues = this.dataQualityIssues.length > 0;
      return;
    }

    if (this.rawHeaders.length > 50) {
      this.dataQualityIssues.push(`Too many columns (${this.rawHeaders.length}). Your data may need to be reformatted. A simple company list should have fewer than 50 columns.`);
    }

    const suspiciousHeaders = this.rawHeaders.filter(h => {
      const str = String(h);
      if (!isNaN(Number(str)) && str.trim() !== '') return true;
      if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(str)) return true;
      if (str.length > 50) return true;
      return false;
    });

    if (suspiciousHeaders.length > this.rawHeaders.length / 2) {
      this.dataQualityIssues.push('The first row doesn\'t look like column headers. Make sure your file has a header row with column names like "Company Name", "Phone", "Address", etc.');
    }

    const unnamedColumns = this.rawHeaders.filter(h => h.startsWith('Column ')).length;
    if (unnamedColumns > this.rawHeaders.length / 2) {
      this.dataQualityIssues.push(`Many columns are unnamed (${unnamedColumns} of ${this.rawHeaders.length}). Please add descriptive column headers to your file.`);
    }

    const rowLengths = this.parsedData.map(row => (row as any[]).filter(cell => cell !== null && cell !== undefined && cell !== '').length);
    // if (rowLengths.length > 0) {
    //   const avgLength = rowLengths.reduce((a, b) => a + b, 0) / rowLengths.length;
    //   const inconsistentRows = rowLengths.filter(len => Math.abs(len - avgLength) > avgLength * 0.5).length;
    //   if (inconsistentRows > this.parsedData.length * 0.3) {
    //     this.dataQualityIssues.push('Many rows have inconsistent data. Some rows have significantly more or fewer filled cells than others. This may indicate merged cells or misaligned data.');
    //   }
    // }

    // const totalCells = this.parsedData.length * this.rawHeaders.length;
    // let filledCells = 0;
    // this.parsedData.forEach(row => {
    //   (row as any[]).forEach(cell => {
    //     if (cell !== null && cell !== undefined && cell !== '') filledCells++;
    //   });
    // });
    // const fillRate = totalCells > 0 ? filledCells / totalCells : 0;
    // if (fillRate < 0.2 && this.parsedData.length > 5) {
    //   this.dataQualityIssues.push(`The data is very sparse (only ${Math.round(fillRate * 100)}% of cells have data). This may indicate the data isn't formatted correctly for import.`);
    // }

    // if (this.rawHeaders.length > 30 && this.parsedData.length < 10) {
    //   this.dataQualityIssues.push('The file has many columns but few rows. This looks like a report or summary, not a company list. Please use a simple list format.');
    // }

    this.hasDataQualityIssues = this.dataQualityIssues.length > 0;
  }

  InitializeFieldMappings(): void {
    const usedFields: string[] = [];

    this.fieldMappings = this.rawHeaders
      .map((header, index) => ({ header, index }))
      // Excluded columns still live in rawHeaders (for the Custom Field builder) but don't get a row
      // in the primary mapping list.
      .filter(({ header }) => !this.excludeFromMapping.includes(header))
      .map(({ header, index }) => {
        const sampleValues = this.parsedData
          .slice(0, 3)
          .map(row => row[index])
          .filter(val => val !== null && val !== undefined && val !== '')
          .map(val => String(val));

        const autoMappedField = this.AutoMapField(header, usedFields);
        if (autoMappedField) {
          usedFields.push(autoMappedField);
        }

        return {
          excelColumn: header,
          companyField: autoMappedField,
          sampleValues: sampleValues,
          columnIndex: index
        };
      });

    this.ApplyPresetCustomFields();
  }

  private ApplyPresetCustomFields(): void {
    const mappedColumns = new Set(this.fieldMappings.filter(m => m.companyField).map(m => m.excelColumn));
    for (const preset of (this.presetCustomFields || [])) {
      if (!this.rawHeaders.includes(preset.sourceColumn) || mappedColumns.has(preset.sourceColumn)) continue;
      if (this.customFieldDefinitions.some(d => d.sourceColumn === preset.sourceColumn)) continue;
      this.customFieldDefinitions.push({ name: preset.name, sourceColumn: preset.sourceColumn, type: preset.type || 'text' });
    }
  }

  // Column names that must never be auto-mapped, even though they'd otherwise match a rule below
  // (e.g. "CompanyID" contains "company" and would falsely match the Company Name rule).
  private readonly noAutoMapHeaders: string[] = ['companyid'];

  // Every temp field ProcessMappings() stores a default_contact_* mapped value under (see
  // companyFields above) - used both to build the pending contact's info and to strip them all back
  // off the row afterward so none of them leak into the payload sent to the companies endpoint.
  private readonly defaultContactExtraFieldKeys: string[] = [
    '_default_contact_first_name', '_default_contact_last_name', '_default_contact_middle_name',
    '_default_contact_email', '_default_contact_phone_number', '_default_contact_cell_phone',
    '_default_contact_alt_phone', '_default_contact_fax', '_default_contact_address',
    '_default_contact_city', '_default_contact_province', '_default_contact_country',
    '_default_contact_postal_code', '_default_contact_website', '_default_contact_custom_id',
    '_default_contact_gender', '_default_contact_birthday', '_default_contact_anniversary',
    '_default_contact_sin', '_default_contact_spoken_language', '_default_contact_custom_data',
  ];

  AutoMapField(header: string, usedFields: string[]): string {
    const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');

    if (this.noAutoMapHeaders.includes(normalizedHeader)) {
      return '';
    }

    const mappingRules: { patterns: string[]; field: string }[] = [
      // All default_contact_* rules must come first - a header like "ContactFax" or "ContactWebsite"
      // would otherwise match the company-level fax/website/etc rules below before ever reaching
      // these (both sets of patterns share substrings like "fax"/"website"/"country"/"id").
      { patterns: ['defaultcontactfirstname', 'contactfirstname'], field: 'default_contact_first_name' },
      { patterns: ['defaultcontactlastname', 'contactlastname'], field: 'default_contact_last_name' },
      { patterns: ['defaultcontactmiddlename', 'contactmiddlename'], field: 'default_contact_middle_name' },
      { patterns: ['defaultcontactemail', 'contactemail', 'primarycontactemail'], field: 'default_contact_email' },
      { patterns: ['defaultcontactphone', 'contactphone', 'primarycontactphone', 'contactphonenumber'], field: 'default_contact_phone_number' },
      { patterns: ['defaultcontactcellphone', 'contactcellphone', 'contactcell', 'contactmobile'], field: 'default_contact_cell_phone' },
      { patterns: ['defaultcontactaltphone', 'contactaltphone', 'contactalternatephone'], field: 'default_contact_alt_phone' },
      { patterns: ['defaultcontactfax', 'contactfax'], field: 'default_contact_fax' },
      { patterns: ['defaultcontactaddress', 'contactaddress', 'contactstreetaddress'], field: 'default_contact_address' },
      { patterns: ['defaultcontactcity', 'contactcity'], field: 'default_contact_city' },
      { patterns: ['defaultcontactprovince', 'contactprovince', 'contactstate'], field: 'default_contact_province' },
      { patterns: ['defaultcontactcountry', 'contactcountry'], field: 'default_contact_country' },
      { patterns: ['defaultcontactpostal', 'contactpostal', 'contactzip', 'contactpostalcode'], field: 'default_contact_postal_code' },
      { patterns: ['defaultcontactwebsite', 'contactwebsite'], field: 'default_contact_website' },
      { patterns: ['defaultcontactcustomid', 'contactcustomid'], field: 'default_contact_custom_id' },
      { patterns: ['defaultcontactgender', 'contactgender'], field: 'default_contact_gender' },
      { patterns: ['defaultcontactbirthday', 'contactbirthday', 'contactbirthdate', 'contactdob'], field: 'default_contact_birthday' },
      { patterns: ['defaultcontactanniversary', 'contactanniversary'], field: 'default_contact_anniversary' },
      { patterns: ['defaultcontactsin', 'contactsin', 'defaultcontactssn', 'contactssn'], field: 'default_contact_sin' },
      { patterns: ['defaultcontactspokenlanguage', 'contactspokenlanguage', 'contactlanguage'], field: 'default_contact_spoken_language' },
      { patterns: ['defaultcontactnotes', 'contactnotes', 'defaultcontactcustomdata', 'contactcustomdata'], field: 'default_contact_custom_data' },
      { patterns: ['defaultcontact', 'primarycontact', 'maincontact', 'contactname', 'contactperson', 'defaultclient'], field: 'default_contact' },
      { patterns: ['companyname', 'businessname', 'firmname', 'organization', 'name'], field: 'name' },
      { patterns: ['yearend', 'yearenddate', 'fiscalyearend', 'fiscalyear'], field: 'year_end_date' },
      { patterns: ['yearendmonth', 'endmonth', 'fiscalmonth'], field: 'year_end_month' },
      { patterns: ['yearendday', 'endday', 'fiscalday'], field: 'year_end_day' },
      { patterns: ['allteammembers', 'visibletoall', 'teamvisibility', 'allteam'], field: 'all_team_members' },
      { patterns: ['isaclient', 'yourclient', 'client', 'isclient', 'activeclient'], field: 'is_a_client' },
      { patterns: ['phone', 'phonenumber', 'telephone', 'tel', 'officephone', 'mainphone'], field: 'phone_number' },
      { patterns: ['fax', 'faxnumber'], field: 'fax' },
      { patterns: ['website', 'web', 'url', 'homepage', 'site'], field: 'website' },
      { patterns: ['address', 'streetaddress', 'street', 'address1', 'addressline1'], field: 'str_address_1' },
      { patterns: ['address2', 'addressline2', 'suite', 'unit', 'apt'], field: 'str_address_2' },
      { patterns: ['city', 'town'], field: 'city' },
      { patterns: ['province', 'state', 'region'], field: 'province' },
      { patterns: ['postal', 'postalcode', 'zip', 'zipcode', 'postcode'], field: 'postal_code' },
      { patterns: ['country', 'nation'], field: 'country' },
      { patterns: ['corpname', 'corporatename', 'legalname', 'registeredname'], field: 'corp_name' },
      { patterns: ['customid', 'id', 'clientid', 'accountid'], field: 'custom_id' },
      { patterns: ['businessnumber', 'business_id', 'taxnumber', 'tax_id'], field: 'business_number' },
      { patterns: ['industry', 'sector', 'field'], field: 'industry' },
      { patterns: ['type', 'companytype', 'businesstype', 'category'], field: 'type' },
      { patterns: ['description', 'notes', 'comments', 'remarks', 'memo'], field: 'description' },
    ];

    for (const rule of mappingRules) {
      if (usedFields.includes(rule.field)) {
        continue;
      }

      for (const pattern of rule.patterns) {
        if (normalizedHeader === pattern || normalizedHeader.includes(pattern)) {
          return rule.field;
        }
      }
    }

    return '';
  }

  UpdateFieldMapping(index: number, companyField: string): void {
    if (this.fieldMappings[index]) {
      this.fieldMappings[index].companyField = companyField;
    }
  }

  GetAvailableFields(currentIndex: number): CompanyField[] {
    const usedFields = this.fieldMappings
      .filter((_, i) => i !== currentIndex)
      .map(m => m.companyField)
      .filter(f => f);

    const currentSelection = this.fieldMappings[currentIndex]?.companyField || '';

    return this.companyFields.filter(field =>
      field.key === '' ||
      field.key === currentSelection ||
      !usedFields.includes(field.key)
    );
  }

  IsFieldAlreadyMapped(fieldKey: string, currentIndex: number): boolean {
    if (!fieldKey) return false;

    return this.fieldMappings.some((mapping, index) =>
      index !== currentIndex && mapping.companyField === fieldKey
    );
  }

  // Matches an existing contact's display_name against an already-normalized (lowercased/trimmed)
  // target name. Checks the raw stored name first, then a version reparsed through the same
  // comma-reversal/prefix-stripping as the import side (see parseNameFromDisplayName) - a contact
  // stored as "Doe, Eric" or "Dr. Eric Doe" would otherwise never match an import value that
  // ProcessMappings has already reformatted to "Eric Doe", wrongly flagging a real contact as new.
  private ClientNameMatches(client: any, normalizedTarget: string): boolean {
    if (!client.display_name) return false;
    const raw = client.display_name.toLowerCase().trim();
    if (raw === normalizedTarget) return true;
    const reformatted = parseNameFromDisplayName(client.display_name).displayName.toLowerCase().trim();
    return !!reformatted && reformatted === normalizedTarget;
  }

  ProcessMappings(): void {
    this.mappedCompanies = [];
    this.validationErrors = [];

    // Dedupes "to be created" default contacts across rows within this same import run - e.g. if
    // "John Smith" is the (unmatched) default contact for 5 different company rows, all 5 share the
    // same pending guid/info here instead of each spawning its own independent placeholder, so
    // ImportCompanies() only ever creates one real "John Smith" contact and links all 5 companies to
    // it (see the pendingContactsByName lookup below and ImportCompanies' createdPendingContacts map).
    const pendingContactsByName = new Map<string, { guid: string; info: any; display_name: string }>();

    // Check if required fields are mapped
    const mappedFields = this.fieldMappings
      .filter(m => m.companyField)
      .map(m => m.companyField);

    const requiredFields = this.companyFields.filter(f => f.required);
    const missingRequired = requiredFields.filter(f => !mappedFields.includes(f.key));

    if (missingRequired.length > 0) {
      this.validationErrors.push(
        `Missing required fields: ${missingRequired.map(f => f.label).join(', ')}`
      );
    }

    // Whether both Default Contact First and Last Name columns are mapped - when Default Contact
    // Display Name itself isn't mapped, this is what triggers the fake "First + Last Name" combined
    // display name fallback below.
    const hasFirstAndLastMapped = mappedFields.includes('default_contact_first_name') && mappedFields.includes('default_contact_last_name');

    // Client Track's own CompanyID column - tracked per row (see merged_ids below) so the backend
    // can trace a Schedule/ContactLinks reference back to whichever Fusion company it ended up as,
    // same idea as clients' merged_ids (see clients/import's ContactID tracking).
    const companyIdColIndex = this.rawHeaders.findIndex(h => h.toLowerCase() === 'companyid');

    // Process each data row
    this.parsedData.forEach((row, rowIndex) => {
      const company: any = {
        // Defaults to true - most bulk imports are of the firm's own clients. Overridden per-row by
        // a mapped "Your Client (Yes/No)" column, or in bulk afterward via SetAllAsClients().
        is_a_client: true,
        name: '',
        description: '',
        phone_number: '',
        fax: '',
        website: '',
        str_address_1: '',
        str_address_2: '',
        city: '',
        province: '',
        postal_code: '',
        country: '',
        corp_name: '',
        custom_id: '',
        business_number: '',
        industry: '',
        type: '',
        clients: [],
        tags: [],
        groups: [],
        team_members: [],
        client_groups: [],
        all_team_members: true,
        year_end_month: '12',
        year_end_day: '31',
        year_end_date: `${new Date().getFullYear()}-12-31`,
        default_client_guid: '',
        // Validation properties
        _errors: [],
        _isValid: true,
        _rowNumber: rowIndex + 2,
        _selected: false,
        _nameMissing: false,
        _clientsMissing: false,
        _defaultClientMissing: false,
        _teamMembersMissing: false,
      };

      if (this.preloadedFile && companyIdColIndex !== -1) {
        const rawCompanyId = row[companyIdColIndex];
        company._mergedCompanyIds = (rawCompanyId !== null && rawCompanyId !== undefined && rawCompanyId !== '')
          ? [String(rawCompanyId)]
          : [];
      }

      // Pre-resolved groups/clients (e.g. from the Client Track import wizard) - added before the
      // column-mapped default-contact logic below so it sees them already there and just fills in
      // default_client_guid/role rather than duplicating.
      if (this.presetGroupsByRow[rowIndex]) company.groups.push(...this.presetGroupsByRow[rowIndex]);
      if (this.presetClientsByRow[rowIndex]) {
        company.clients.push(...this.presetClientsByRow[rowIndex]);
        // Client Track imports only - a preset client tagged is_default_for_company (see
        // CtdBuildCompanyLinkedClients) sets the default directly, skipping the name-matching below.
        const presetDefault = this.presetClientsByRow[rowIndex].find((c: any) => c.is_default_for_company);
        if (presetDefault) company.default_client_guid = presetDefault.guid;
      }

      this.fieldMappings.forEach((mapping) => {
        const colIndex = mapping.columnIndex;
        if (mapping.companyField && row[colIndex] !== undefined && row[colIndex] !== null) {
          let value = row[colIndex];

          if (typeof value === 'number') {
            value = String(value);
          } else if (value instanceof Date) {
            value = value.toISOString().split('T')[0];
          } else {
            value = String(value).trim();
          }

          // Handle boolean fields (is_a_client, all_team_members)
          if (mapping.companyField === 'is_a_client' || mapping.companyField === 'all_team_members') {
            const lowerValue = value.toLowerCase();
            company[mapping.companyField] = lowerValue === 'yes' || lowerValue === 'true' || lowerValue === '1' || lowerValue === 'y';
          }
          // Handle year_end_date - parse into month and day
          else if (mapping.companyField === 'year_end_date') {
            company.year_end_date = value;
            // Try to parse the date into month and day
            const parsed = this.ParseYearEndDate(value);
            if (parsed) {
              company.year_end_month = parsed.month;
              company.year_end_day = parsed.day;
            }
          }
          // Handle year_end_month - ensure it's zero-padded
          else if (mapping.companyField === 'year_end_month') {
            const monthNum = parseInt(value, 10);
            if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
              company.year_end_month = monthNum < 10 ? '0' + monthNum : String(monthNum);
            }
          }
          // Handle year_end_day - ensure it's zero-padded
          else if (mapping.companyField === 'year_end_day') {
            const dayNum = parseInt(value, 10);
            if (!isNaN(dayNum) && dayNum >= 1 && dayNum <= 31) {
              company.year_end_day = dayNum < 10 ? '0' + dayNum : String(dayNum);
            }
          }
          else if (mapping.companyField === 'default_contact') {
            company._default_contact_name = value;
          }
          // Every other default_contact_* field (first/last/middle name, email, phone numbers,
          // address, custom_id, gender, birthday, etc - see companyFields above) is held on the row
          // as a plain temp value with a leading underscore - only ever consumed below, alongside
          // default_contact, when building the new contact's create payload. Discarded otherwise
          // (see the unconditional cleanup right after the default-contact matching block below).
          else if (mapping.companyField.startsWith('default_contact_')) {
            company['_' + mapping.companyField] = value;
          }
          else {
            company[mapping.companyField] = value;
          }
        }
      });

      // Build custom_data_arr from user-defined custom field definitions
      this.customFieldDefinitions.forEach(def => {
        if (def.name && def.sourceColumn) {
          const colIndex = this.rawHeaders.indexOf(def.sourceColumn);
          if (colIndex !== -1) {
            const val = row[colIndex];
            if (!company.custom_data_arr) { company.custom_data_arr = []; }
            company.custom_data_arr.push({
              name: def.name,
              type: def.type || 'text',
              value: val !== null && val !== undefined ? String(val).trim() : ''
            });
          }
        }
      });

      // Multi-column Display Name builder - disabled, see displayNameParts/AddDisplayNamePart/etc
      // below and the commented-out card in import.page.html. Simplified per request: Default
      // Contact Display Name column if mapped, else a fake First + Last Name combined column if
      // both of those are mapped.
      // if (!company._default_contact_name && this.displayNameParts.length > 0) {
      //   const builtParts = this.displayNameParts.map(colName => {
      //     const colIndex = this.rawHeaders.indexOf(colName);
      //     if (colIndex === -1) return '';
      //     const val = row[colIndex];
      //     return val !== null && val !== undefined ? String(val).trim() : '';
      //   }).filter(v => v !== '');
      //   if (builtParts.length > 0) {
      //     company._default_contact_name = builtParts.join(this.displayNameSeparator ?? ' ');
      //   }
      // }

      // Fallback: no Default Contact Display Name column mapped - if both Default Contact First and
      // Last Name columns are mapped, treat their combined value as a fake Display Name column.
      if (!company._default_contact_name && hasFirstAndLastMapped) {
        company._default_contact_name = [company._default_contact_first_name, company._default_contact_last_name]
          .filter(n => n)
          .join(' ');
      }

      // Match default contact from explicit mapped column (takes priority). Parse it with the same
      // name-splitting logic as the Clients import wizard so "Doe, Eric" / "Dr. Eric Doe" etc.
      // match an existing contact correctly. A joint name ("Eric and Jane Doe") only ever keeps
      // the primary half here - unlike the Clients import, this never spawns a second contact.
      if (company._default_contact_name) {
        const parsedPrimary = parseNamesFromDisplayName(company._default_contact_name as string).primary;
        const parsedDisplayName = (parsedPrimary.displayName || (company._default_contact_name as string).trim());
        const normalized = parsedDisplayName.toLowerCase().trim();
        const matches = this.all_available_clients.filter(c => this.ClientNameMatches(c, normalized));
        if (matches.length === 1) {
          const alreadyAssigned = company.clients.some((c: any) => c.guid === matches[0].guid);
          if (!alreadyAssigned) {
            company.clients.push({ ...matches[0], role_guid: this.highestRoleGuid });
          } else {
            const existing = company.clients.find((c: any) => c.guid === matches[0].guid);
            if (existing) existing.role_guid = this.highestRoleGuid;
          }
          company.default_client_guid = matches[0].guid;
        } else if (matches.length > 1) {
          // More than one existing contact shares this exact name (e.g. several "Charles Morin"
          // records) - too risky to guess which one is meant, and definitely wrong to create a new
          // duplicate contact when we know for certain at least one of these IS the real match.
          // Leave this row with no default contact selected (same as an unmapped row) so it's
          // flagged by the existing "no default contact selected" validation and the user picks the
          // right one themselves via the "click to add" search modal in the review step.
          company._ambiguousDefaultContactMatches = matches.length;
        } else if (parsedDisplayName) {
          // No existing contact matched by name - reuse an already-pending placeholder for this
          // exact name if an earlier row in this same import already created one (see
          // pendingContactsByName above), otherwise build a new "to be created" placeholder contact
          // right here (front-end only, nothing hits the API yet) from the parsed name plus whatever
          // default_contact_* columns were mapped (explicit First/Last/Middle Name columns win over
          // the name parsed from the Display Name), and select it as the default contact immediately.
          // It shows up in the review step's contact list/dropdown like any other contact - no need
          // to click "Quick Create Default Contacts" first any more. ImportCompanies() creates it for
          // real (see the pending-contact check there) right before creating the company it belongs
          // to; "Quick Create Default Contacts" now only ever has to backfill rows that still end up
          // with no default contact at all (no name mapped, or mapped but never matched/replaced here).
          let pending = pendingContactsByName.get(normalized);
          if (!pending) {
            pending = {
              guid: `__pending_contact_${company._rowNumber}__`,
              display_name: parsedDisplayName,
              info: {
                first_name: (company._default_contact_first_name as string) || parsedPrimary.firstName || 'Default',
                last_name: (company._default_contact_last_name as string) || parsedPrimary.lastName || 'Contact',
                middle_name: (company._default_contact_middle_name as string) || parsedPrimary.middleName || '',
                display_name: parsedDisplayName,
                email: (company._default_contact_email as string) || '',
                phone_number: (company._default_contact_phone_number as string) || '',
                cell_phone: (company._default_contact_cell_phone as string) || '',
                alt_phone: (company._default_contact_alt_phone as string) || '',
                fax: (company._default_contact_fax as string) || '',
                str_address: (company._default_contact_address as string) || '',
                city: (company._default_contact_city as string) || '',
                province: (company._default_contact_province as string) || '',
                country: (company._default_contact_country as string) || '',
                postal_code: (company._default_contact_postal_code as string) || '',
                website: (company._default_contact_website as string) || '',
                custom_id: (company._default_contact_custom_id as string) || '',
                gender: (company._default_contact_gender as string) || '',
                birthday: this.ParseFullDateString(company._default_contact_birthday as string),
                anniversary: this.ParseFullDateString(company._default_contact_anniversary as string),
                sin: (company._default_contact_sin as string) || '',
                spoken_language: (company._default_contact_spoken_language as string) || '',
                custom_data: (company._default_contact_custom_data as string) || ''
              }
            };
            pendingContactsByName.set(normalized, pending);
          }
          company.clients.push({
            guid: pending.guid,
            display_name: `${pending.display_name} (new contact)`,
            role_guid: this.highestRoleGuid,
            _pending_contact: true,
            _pending_contact_info: pending.info
          });
          company.default_client_guid = pending.guid;
        }
        delete company._default_contact_name;
      // Fallback: auto-match by company name only if no clients assigned yet
      } else if (company.name && company.name.trim() !== '' && company.clients.length === 0) {
        const normalized = company.name.toLowerCase().trim();
        const matches = this.all_available_clients.filter(c => this.ClientNameMatches(c, normalized));
        if (matches.length === 1) {
          company.clients = [{ ...matches[0], role_guid: this.highestRoleGuid }];
          company.default_client_guid = matches[0].guid;
        }
      }
      // The default_contact_* extra fields only ever mean anything paired with a Default Contact
      // Display Name mapping (handled above) - strip them here regardless of which branch ran above,
      // so a stray mapped column with no display name column never leaks into the company payload.
      this.defaultContactExtraFieldKeys.forEach(key => delete company[key]);

      this.mappedCompanies.push(company);
    });

    // Reset validation state
    this.isValidated = false;
    this.hasValidationErrors = false;
    this.validCompaniesCount = 0;
    this.invalidCompaniesCount = 0;
    this.showErrorsOnly = false;

    // Reset pagination
    this.currentPage = 1;

    // Detect duplicate names
    this.DetectDuplicateNames();

    // Set preview
    this.previewCompanies = this.mappedCompanies.slice(0, 10);
  }

  DetectDuplicateNames(): void {
    const nameCounts: { [name: string]: number[] } = {};

    this.mappedCompanies.forEach((company, index) => {
      const companyName = (company.name || '').toLowerCase().trim();

      if (companyName) {
        if (!nameCounts[companyName]) {
          nameCounts[companyName] = [];
        }
        nameCounts[companyName].push(index);
      }
    });

    this.duplicateNames = Object.entries(nameCounts)
      .filter(([_, indices]) => indices.length > 1)
      .map(([name, indices]) => ({ name, indices }));

    this.hasDuplicateNames = this.duplicateNames.length > 0;
  }

  RemoveDuplicateNames(): void {
    const indicesToRemove = new Set<number>();

    this.duplicateNames.forEach(dup => {
      dup.indices.slice(1).forEach(index => indicesToRemove.add(index));
    });

    const sortedIndices = Array.from(indicesToRemove).sort((a, b) => b - a);
    sortedIndices.forEach(index => {
      this.mappedCompanies.splice(index, 1);
    });

    this.DetectDuplicateNames();
    this.isValidated = false;
    this.currentPage = 1;
  }

  // Remove companies whose name matches an existing company already in the system
  RemoveExistingNameMatches(): void {
    const rowsToRemove = new Set(this.existingNameMatches.map(m => m.rowNumber));
    this.mappedCompanies = this.mappedCompanies.filter(c => !rowsToRemove.has(c._rowNumber));
    this.existingNameMatches = [];
    this.hasExistingNameMatches = false;
    this.isValidated = false;
    this.DetectDuplicateNames();
    this.currentPage = 1;
  }

  async ValidateCompanies(): Promise<void> {
    this.isValidating = true;
    this.showErrorsOnly = false; // Start each fresh validation run unfiltered
    this.currentPage = 1;
    this.validationErrors = [];
    this.hasValidationErrors = false;
    this.validCompaniesCount = 0;
    this.invalidCompaniesCount = 0;
    this.existingNameMatches = [];
    this.hasExistingNameMatches = false;

    // Fetch existing companies to check for name matches
    try {
      const existingCompaniesResponse: any = await this.apiService.get('companies');
      if (existingCompaniesResponse.success && existingCompaniesResponse.data) {
        this.existingCompanyNames = existingCompaniesResponse.data
          .filter((c: any) => c.name && c.name.trim() !== '')
          .map((c: any) => c.name.toLowerCase().trim());
      }
    } catch (error) {
      console.error('Error fetching existing companies:', error);
      this.existingCompanyNames = [];
    }


    // Validate each company
    this.mappedCompanies.forEach((company) => {
      company._errors = [];
      company._isValid = true;
      company._nameMissing = false;
      company._clientsMissing = false;
      company._defaultClientMissing = false;
      company._teamMembersMissing = false;
      company._nameMatchWarning = false;
      company._warnings = [];

      // Company name is required
      if (!company.name || company.name.trim() === '') {
        company._errors.push('Company name is required');
        company._nameMissing = true;
      }

      // At least one contact must be assigned
      if (!company.clients || company.clients.length === 0) {
        company._errors.push('At least one contact must be assigned');
        company._clientsMissing = true;
      }

      // Default contact must be selected
      if (!company.default_client_guid || company.default_client_guid.trim() === '') {
        company._errors.push('A default contact must be selected');
        company._defaultClientMissing = true;
      } else if (company.clients && company.clients.length > 0) {
        const defaultExists = company.clients.some(c => c.guid === company.default_client_guid);
        if (!defaultExists) {
          company._errors.push('Selected default contact is not in the assigned contacts list');
          company._defaultClientMissing = true;
        }
      }

      // Check for matching company names with existing companies (warning only)
      const importCompanyName = (company.name || '').toLowerCase().trim();
      if (importCompanyName && this.existingCompanyNames.includes(importCompanyName)) {
        company._nameMatchWarning = true;
        company._warnings.push(`A company with the name "${company.name}" already exists`);
        this.existingNameMatches.push({
          rowNumber: company._rowNumber,
          companyName: company.name
        });
      }

      // Team members required if not visible to all
      if (!company.all_team_members && (!company.team_members || company.team_members.length === 0)) {
        company._errors.push('At least one team member must be assigned when not visible to all');
        company._teamMembersMissing = true;
      }

      // Update validity status
      if (company._errors.length > 0) {
        company._isValid = false;
        this.invalidCompaniesCount++;
        this.hasValidationErrors = true;
      } else {
        this.validCompaniesCount++;
      }
    });

    // Build summary
    if (this.invalidCompaniesCount > 0) {
      this.validationErrors.push(`${this.invalidCompaniesCount} company(ies) have validation errors`);
    }

    this.companiesWithoutContacts = this.mappedCompanies.filter(c => !c.clients || c.clients.length === 0).length;
    this.companiesWithoutDefaultContact = this.mappedCompanies.filter(c => !c.default_client_guid || c.default_client_guid.trim() === '').length;
    this.companiesWithAmbiguousDefaultContact = this.mappedCompanies.filter(c => !!c._ambiguousDefaultContactMatches).length;

    if (this.companiesWithoutContacts > 0) {
      this.validationErrors.push(`${this.companiesWithoutContacts} company(ies) have no contacts assigned`);
    }
    if (this.companiesWithAmbiguousDefaultContact > 0) {
      this.validationErrors.push(`${this.companiesWithAmbiguousDefaultContact} company(ies) have a default contact name that matches more than one existing contact - please pick the correct one manually`);
    }
    if (this.companiesWithoutDefaultContact > this.companiesWithAmbiguousDefaultContact) {
      this.validationErrors.push(`${this.companiesWithoutDefaultContact - this.companiesWithAmbiguousDefaultContact} company(ies) have no default contact selected`);
    }

    // Check for existing name matches (warning only, not an error)
    if (this.existingNameMatches.length > 0) {
      this.hasExistingNameMatches = true;
    }

    this.isValidated = true;
    this.isValidating = false;
  }

  OnDataChanged(): void {
    this.isValidated = false;
  }

  async SetAllAsClients(isClient: boolean): Promise<void> {
    const selectedCount = this.GetSelectedCompaniesCount();
    if (selectedCount > 0) {
      this.mappedCompanies.filter(c => c._selected).forEach(company => {
        company.is_a_client = isClient;
      });
      this.OnDataChanged();
      return;
    }
    const alert = await this.alertController.create({
      header: 'Mark as Client',
      message: `Are you sure you want to set all companies as ${isClient ? 'clients' : 'non-clients'}?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: () => {
            this.mappedCompanies.forEach(company => {
              company.is_a_client = isClient;
            });
            this.OnDataChanged();
          }
        }
      ]
    });
    await alert.present();
  }

  async SetAllAsTeamMembers(allow: boolean): Promise<void> {
    const selectedCount = this.GetSelectedCompaniesCount();
    if (selectedCount > 0) {
      this.mappedCompanies.filter(c => c._selected).forEach(company => {
        company.all_team_members = allow;
      });
      this.OnDataChanged();
      return;
    }
    const alert = await this.alertController.create({
      header: 'All Team Members',
      message: `Are you sure you want to set all companies to ${allow ? 'be visible to all team members' : 'restrict to assigned team members'}?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: () => {
            this.mappedCompanies.forEach(company => {
              company.all_team_members = allow;
            });
            this.OnDataChanged();
          }
        }
      ]
    });
    await alert.present();
  }

  // Bulk set year end month and day for all companies
  async SetAllYearEnd(): Promise<void> {
    if (!this.bulkYearEndMonth || !this.bulkYearEndDay) return;
    const selectedCount = this.GetSelectedCompaniesCount();
    if (selectedCount > 0) {
      this.mappedCompanies.filter(c => c._selected).forEach(company => {
        company.year_end_month = this.bulkYearEndMonth;
        company.year_end_day = this.bulkYearEndDay;
      });
      this.OnDataChanged();
      return;
    }
    const alert = await this.alertController.create({
      header: 'Set Year End Date',
      message: `Are you sure you want to set the year end date for all companies to ${this.bulkYearEndMonth}/${this.bulkYearEndDay}?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: () => {
            this.mappedCompanies.forEach(company => {
              company.year_end_month = this.bulkYearEndMonth;
              company.year_end_day = this.bulkYearEndDay;
            });
            this.OnDataChanged();
          }
        }
      ]
    });
    await alert.present();
  }

  // Month name/abbreviation -> numeric month, for the free-text branch of ParseYearEndDate below.
  // Longer/more specific keys ('sept', 'sep') are both kept since we match by whole word, not prefix.
  private readonly monthNameMap: { [key: string]: number } = {
    'january': 1, 'jan': 1,
    'february': 2, 'feb': 2,
    'march': 3, 'mar': 3,
    'april': 4, 'apr': 4,
    'may': 5,
    'june': 6, 'jun': 6,
    'july': 7, 'jul': 7,
    'august': 8, 'aug': 8,
    'september': 9, 'sep': 9, 'sept': 9,
    'october': 10, 'oct': 10,
    'november': 11, 'nov': 11,
    'december': 12, 'dec': 12
  };

  private DaysInMonth(month: number): number {
    // Uses the current year for leap-year purposes - Year End Date has no year of its own (it's a
    // recurring annual date), so this is just a reasonable default for clamping Feb 29 in a leap year.
    return new Date(new Date().getFullYear(), month, 0).getDate();
  }

  private FormatMonthDay(month: number, day: number): { month: string; day: string } | null {
    if (!(month >= 1 && month <= 12)) return null;
    const clampedDay = Math.min(Math.max(day, 1), this.DaysInMonth(month));
    return {
      month: month < 10 ? '0' + month : String(month),
      day: clampedDay < 10 ? '0' + clampedDay : String(clampedDay)
    };
  }

  // Resolves month/day from two or three purely-numeric parts (already split on /, -, ., or spaces),
  // auto-detecting which part is which since months only run 1-12 while days run 1-31: whichever
  // part can't be a month (>12) must be the day. The third part in a 3-part date is the year, which
  // is irrelevant here (Year End Date has no year of its own) - it's identified and discarded rather
  // than fed into month/day resolution. When both remaining parts are <=12 (e.g. "5/7", or the
  // 2-digit-year case in "26-12-31" vs "31-12-26" where the non-month numbers could each pass for
  // day or year), the ambiguity can't be resolved from range alone - see the comments below for the
  // specific tie-breaking guess made in each case.
  private ResolveMonthDayFromParts(parts: number[]): { month: number; day: number } | null {
    if (parts.some(p => isNaN(p) || p < 1)) return null;

    if (parts.length === 2) {
      const [a, b] = parts;
      if (a > 12 && a <= 31 && b <= 12) return { month: b, day: a };
      if (b > 12 && b <= 31 && a <= 12) return { month: a, day: b };
      if (a > 31 || b > 31) return null;
      // Both <=12: genuinely ambiguous ("5/7") - default to Month/Day (matches the existing
      // MM/DD-first convention already used elsewhere in this import, e.g. the example template).
      return { month: a, day: b };
    }

    if (parts.length === 3) {
      // Any part >31 can't be a month or a day, so it's unambiguously the year (handles a 4-digit
      // year in any position). A 2-digit year that happens to land in 1-31 is inherently ambiguous
      // with a day number and is handled in the else branch below.
      const overThirtyOne = parts.map((v, i) => ({ v, i })).filter(p => p.v > 31);
      if (overThirtyOne.length >= 1) {
        const yearIndex = overThirtyOne[0].i;
        const [i1, i2] = [0, 1, 2].filter(i => i !== yearIndex);
        const v1 = parts[i1], v2 = parts[i2];
        if (v1 > 12 && v1 <= 31 && v2 <= 12) return { month: v2, day: v1 };
        if (v2 > 12 && v2 <= 31 && v1 <= 12) return { month: v1, day: v2 };
        if (v1 > 31 || v2 > 31) return null;
        return { month: v1, day: v2 }; // both <=12 - read in original left-to-right order.
      }

      // All three parts are 1-31 (a short/2-digit year, ambiguous with a day number by range
      // alone). How many of the three COULD be a month (<=12) determines which sub-case this is.
      const monthCandidates = parts.map((v, i) => ({ v, i })).filter(p => p.v <= 12);
      if (monthCandidates.length === 0) return null; // nothing here could be a month at all.

      if (monthCandidates.length === 1) {
        // Exactly one value can be a month, e.g. "26-12-31" or "31-12-26" (both meant to read as
        // Dec 31, with a 2-digit year of 26 in either position) - unambiguous month, and of the
        // two remaining numbers, assume the larger is the day and the smaller is the year. Best-
        // effort guess: an actual day-of-month tends to run higher than an arbitrary 2-digit year
        // once the month itself is pinned down. This is what makes "26-12-31" and "31-12-26" both
        // resolve to Dec 31 regardless of which side the year lands on.
        const monthIdx = monthCandidates[0].i;
        const others = [0, 1, 2].filter(i => i !== monthIdx);
        const dayIdx = parts[others[0]] >= parts[others[1]] ? others[0] : others[1];
        return { month: parts[monthIdx], day: parts[dayIdx] };
      }

      if (monthCandidates.length === 2) {
        // Two values could each be a month, e.g. "11/1/21" or "1/11/21" - a real month and day
        // can't both be >12 at once, so the single remaining value (>12) is almost certainly the
        // short year, not either date component. Read the two month-shaped values in their
        // original left-to-right order as month then day (the standard M/D/Y convention), e.g.
        // "11/1/21" -> month=11, day=1 (year=21 discarded).
        const [monthIdx, dayIdx] = monthCandidates.map(c => c.i).sort((a, b) => a - b);
        return { month: parts[monthIdx], day: parts[dayIdx] };
      }

      // All three values are <=12 (fully ambiguous, e.g. "5/7/9") - read left-to-right as
      // month/day (discarding the third as year), matching the Month/Day-first convention used
      // for the plain 2-part case above.
      return { month: parts[0], day: parts[1] };
    }

    return null;
  }

  // Parses a Year End Date cell in essentially any common format into month/day components:
  // month name alone ("Dec", "December" - assumes the last day of that month), month name + day in
  // either order ("Dec 31", "31 Dec"), and 2- or 3-part numeric dates with the separator being /, -,
  // ., or whitespace, in any month/day/year ordering ("12/31", "31/12", "12/31/26", "12-31-26",
  // "26-12-31", "31-12-26", "YYYY-MM-DD", etc.) - see ResolveMonthDayFromParts for how the ordering
  // is auto-detected.
  ParseYearEndDate(value: string): { month: string; day: string } | null {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;

    // Month name only, e.g. "Dec" or "December" (with or without a trailing period) - no day given,
    // so assume the last day of that month.
    const monthOnlyMatch = trimmed.match(/^([a-z]+)\.?$/i);
    if (monthOnlyMatch && this.monthNameMap[monthOnlyMatch[1].toLowerCase()]) {
      const month = this.monthNameMap[monthOnlyMatch[1].toLowerCase()];
      return this.FormatMonthDay(month, this.DaysInMonth(month));
    }

    // Month name + day, in either order, e.g. "Dec 31" or "31 Dec" (also "December 31st" - the
    // ordinal suffix is just ignored since we only look for digits).
    const monthNameToken = Object.keys(this.monthNameMap)
      .sort((a, b) => b.length - a.length) // longer names first so 'sept' isn't shadowed by 'sep'
      .find(name => new RegExp(`\\b${name}\\b`, 'i').test(trimmed));
    if (monthNameToken) {
      const month = this.monthNameMap[monthNameToken];
      const dayMatch = trimmed.replace(new RegExp(monthNameToken, 'i'), '').match(/\d{1,2}/);
      const day = dayMatch ? parseInt(dayMatch[0], 10) : this.DaysInMonth(month);
      return this.FormatMonthDay(month, day);
    }

    // Otherwise, treat it as a purely numeric date and split on the usual separators.
    const numericParts = trimmed.split(/[\/\-.\s]+/).filter(p => p !== '').map(p => parseInt(p, 10));
    const resolved = this.ResolveMonthDayFromParts(numericParts);
    if (resolved) return this.FormatMonthDay(resolved.month, resolved.day);

    return null;
  }

  // Parses a full date (with year) - Default Contact Birthday/Anniversary - into a SQL-safe
  // "YYYY-MM-DD" string, or '' if it can't be confidently parsed. Unlike ParseYearEndDate above,
  // an unparseable value here must become '' rather than null - the backend's update_client skips
  // an empty birthday/anniversary safely, but hands a non-empty non-date string straight to SQL
  // Server as-is with no validation, which is exactly what was causing "Conversion failed when
  // converting date and/or time from character string" 500s (e.g. a raw Excel serial number like
  // "32874" coming through as a string when the column wasn't formatted as a date in the sheet -
  // a genuinely date-formatted Excel cell is already handled upstream via cellDates: true).
  ParseFullDateString(value: string): string {
    if (!value) return '';
    const trimmed = String(value).trim();
    if (!trimmed) return '';

    // Already ISO (optionally with a time component tacked on) - keep just the date part.
    const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      const formatted = this.FormatMonthDay(parseInt(m, 10), parseInt(d, 10));
      return formatted ? `${y}-${formatted.month}-${formatted.day}` : '';
    }

    // A bare number with no separators - almost certainly an Excel serial date (the sheet's column
    // wasn't actually formatted as a date, so XLSX handed back the raw serial instead of a JS Date).
    // Excel's epoch is 1899-12-30 (its own leap-year bug baked in) - this is the standard conversion.
    if (/^\d{4,6}$/.test(trimmed)) {
      const serial = parseInt(trimmed, 10);
      const asDate = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
      if (!isNaN(asDate.getTime()) && asDate.getUTCFullYear() > 1900 && asDate.getUTCFullYear() < 2100) {
        return asDate.toISOString().split('T')[0];
      }
      return '';
    }

    // Otherwise, split on the usual separators and resolve month/day/year the same way
    // ResolveMonthDayFromParts does for Year End Date, but keeping (rather than discarding) the year.
    const numericParts = trimmed.split(/[\/\-.\s]+/).filter(p => p !== '').map(p => parseInt(p, 10));
    if (numericParts.length === 3 && numericParts.every(p => !isNaN(p))) {
      const yearIdx = numericParts.findIndex(p => p > 31);
      let year: number, monthDayParts: number[];
      if (yearIdx !== -1) {
        year = numericParts[yearIdx];
        monthDayParts = numericParts.filter((_, i) => i !== yearIdx);
      } else {
        // No unambiguous 4-digit year - assume the last part is a 2-digit year (matches the M/D/Y
        // convention used elsewhere in this file) and expand it to a full year.
        const shortYear = numericParts[2];
        year = shortYear + (shortYear < 50 ? 2000 : 1900);
        monthDayParts = [numericParts[0], numericParts[1]];
      }
      const resolved = this.ResolveMonthDayFromParts(monthDayParts);
      if (resolved) {
        const formatted = this.FormatMonthDay(resolved.month, resolved.day);
        if (formatted) return `${year}-${formatted.month}-${formatted.day}`;
      }
    }

    // Last resort - let the browser's own date parser take a swing (handles things like
    // "January 15, 1990" that fell through everything above).
    const nativeParsed = new Date(trimmed);
    if (!isNaN(nativeParsed.getTime())) {
      return nativeParsed.toISOString().split('T')[0];
    }

    return '';
  }

  RemoveFile(event: Event): void {
    event.stopPropagation();
    this.uploadedFile = null;
    this.filePreviewData = null;
    this.uploadError = null;
    this.parsedData = [];
    this.rawHeaders = [];
    this.fieldMappings = [];
    this.dataQualityIssues = [];
    this.hasDataQualityIssues = false;
    this.customFieldDefinitions = [];
    this.displayNameParts = [];
    this.displayNameSeparator = ' ';
    this.displayNameColumnToAdd = '';
  }

  FormatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  DownloadExampleTemplate(): void {
    // Create headers from company fields (excluding the placeholder)
    const headers = this.companyFields
      .filter(f => f.key !== '')
      .map(f => f.label.replace(/ \(.*\)$/, '')); // Remove hints like "(Yes/No)" from labels

    // Create example data row
    const exampleData = [
      'ABC Company Inc. DELETE THIS ROW BEFORE USING',     // Company Name
      '12/31',                // Year End Date
      '12',                   // Year End Month
      '31',                   // Year End Day
      'Yes',                  // Visible to All Team Members
      'Yes',                  // Your Client
      '555-123-4567',         // Phone Number
      '555-123-4568',         // Fax
      'www.abccompany.com',   // Website
      '123 Main Street',      // Street Address
      'Suite 100',            // Street Address Line 2
      'Toronto',              // City
      'ON',                   // Province/State
      'M5V 1A1',              // Postal/Zip Code
      'Canada',               // Country
      'ABC Corporation',      // Corporate/Legal Name
      'ABC-001',              // Custom ID
      'BN123456789',          // Business Number
      'Technology',           // Industry
      'Corporation',          // Company Type
      'Example company notes' // Notes/Description
    ];

    // Create worksheet with headers and example row
    const worksheetData = [headers, exampleData];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = headers.map(() => ({ wch: 25 }));

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Companies');

    // Generate and download the file
    XLSX.writeFile(workbook, 'Fusionworkflow_Company_Import_Template.xlsx');
  }

  AddCustomImportField(): void {
    this.customFieldDefinitions.push({ name: '', sourceColumn: '' });
  }

  RemoveCustomImportField(index: number): void {
    this.customFieldDefinitions.splice(index, 1);
  }

  // Columns not currently used by a primary field mapping and not already claimed by a custom field
  // definition - includes columns hidden from the primary list entirely (see excludeFromMapping).
  GetUnmappedColumns(): string[] {
    const mappedColumns = new Set(this.fieldMappings.filter(m => m.companyField).map(m => m.excelColumn));
    const usedAsCustomField = new Set(this.customFieldDefinitions.filter(d => d.sourceColumn).map(d => d.sourceColumn));
    return this.rawHeaders.filter(h => !mappedColumns.has(h) && !usedAsCustomField.has(h));
  }

  MapAllUnmappedFields(): void {
    this.GetUnmappedColumns().forEach(col => {
      this.customFieldDefinitions.push({ name: col, sourceColumn: col });
    });
  }

  // Default Contact display name builder methods - mirrors clients/import's identical methods.
  IsDisplayNameMapped(): boolean {
    return this.fieldMappings.some(m => m.companyField === 'default_contact');
  }

  // Whether ProcessMappings will actually be able to resolve a default contact display name for
  // this sheet - directly mapped, or derivable from Default Contact First + Last Name (see the
  // hasFirstAndLastMapped fallback there). Used to hide the "these fields are ignored without a
  // Default Contact Display Name column" note once it's no longer true.
  CanResolveDefaultContactName(): boolean {
    if (this.IsDisplayNameMapped()) return true;
    const mappedFields = this.fieldMappings.filter(m => m.companyField).map(m => m.companyField);
    return mappedFields.includes('default_contact_first_name') && mappedFields.includes('default_contact_last_name');
  }

  GetColumnsNotInDisplayNameParts(): string[] {
    return this.rawHeaders.filter(h => !this.displayNameParts.includes(h));
  }

  AddDisplayNamePart(col: string): void {
    if (col && !this.displayNameParts.includes(col)) {
      this.displayNameParts.push(col);
      this.displayNameColumnToAdd = '';
    }
  }

  RemoveDisplayNamePart(index: number): void {
    this.displayNameParts.splice(index, 1);
  }

  MoveDisplayNamePartUp(index: number): void {
    if (index > 0) {
      const temp = this.displayNameParts[index];
      this.displayNameParts[index] = this.displayNameParts[index - 1];
      this.displayNameParts[index - 1] = temp;
    }
  }

  MoveDisplayNamePartDown(index: number): void {
    if (index < this.displayNameParts.length - 1) {
      const temp = this.displayNameParts[index];
      this.displayNameParts[index] = this.displayNameParts[index + 1];
      this.displayNameParts[index + 1] = temp;
    }
  }

  GetDisplayNamePreview(): string {
    if (this.displayNameParts.length === 0 || this.parsedData.length === 0) return '';
    const firstRow = this.parsedData[0];
    const parts = this.displayNameParts.map(colName => {
      const colIndex = this.rawHeaders.indexOf(colName);
      if (colIndex === -1) return '';
      const val = firstRow[colIndex];
      return val !== null && val !== undefined ? String(val).trim() : '';
    }).filter(v => v !== '');
    return parts.join(this.displayNameSeparator ?? ' ');
  }

  get highestRoleGuid(): string {
    if (!this.all_available_roles || this.all_available_roles.length === 0) return '0';
    return this.all_available_roles.reduce((prev, curr) => (prev.level < curr.level ? prev : curr)).guid;
  }

  GetFieldLabel(fieldKey: string): string {
    const field = this.companyFields.find(f => f.key === fieldKey);
    return field ? field.label : fieldKey;
  }

  GetMappedFieldsCount(): number {
    return this.fieldMappings.filter(m => m.companyField).length;
  }

  HasRequiredFieldsMapped(): boolean {
    const mappedFields = this.fieldMappings
      .filter(m => m.companyField)
      .map(m => m.companyField);

    const requiredFields = this.companyFields.filter(f => f.required);
    return requiredFields.every(f => mappedFields.includes(f.key));
  }

  // Companies actually shown in the table/cards - every mapped company normally, or just the ones
  // with validation errors when showErrorsOnly is toggled on. Deliberately NOT gated on isValidated -
  // editing a company flips isValidated to false immediately (see OnDataChanged), but _isValid itself
  // stays put until the next explicit Validate click, so staying filtered on it (rather than snapping
  // back to "All" on every keystroke) is what lets you actually work through the error list.
  get filteredCompanies(): any[] {
    return this.showErrorsOnly
      ? this.mappedCompanies.filter(c => !c._isValid)
      : this.mappedCompanies;
  }

  SetShowErrorsOnly(value: boolean): void {
    this.showErrorsOnly = value;
    this.currentPage = 1;
  }

  // Pagination methods
  get paginatedCompanies(): any[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredCompanies.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredCompanies.length / this.pageSize);
  }

  get startRecord(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endRecord(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredCompanies.length);
  }

  GoToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  NextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  PreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  GetPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Selection methods
  GetSelectedCompaniesCount(): number {
    return this.mappedCompanies.filter(c => c._selected).length;
  }

  AreAllCompaniesSelected(): boolean {
    return this.mappedCompanies.length > 0 && this.mappedCompanies.every(c => c._selected);
  }

  ToggleSelectAllCompanies(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.mappedCompanies.forEach(company => company._selected = checked);
  }

  DeleteCompany(index: number): void {
    // Look up by object reference rather than page math - paginatedCompanies is a slice of
    // filteredCompanies, not mappedCompanies directly, once showErrorsOnly is in play.
    const company = this.paginatedCompanies[index];
    const actualIndex = company ? this.mappedCompanies.indexOf(company) : -1;
    if (actualIndex !== -1) {
      this.mappedCompanies.splice(actualIndex, 1);
      this.isValidated = false;
      this.DetectDuplicateNames();

      if (this.currentPage > this.totalPages && this.totalPages > 0) {
        this.currentPage = this.totalPages;
      }
    }
  }

  DeleteSelectedCompanies(): void {
    this.mappedCompanies = this.mappedCompanies.filter(c => !c._selected);
    this.isValidated = false;
    this.DetectDuplicateNames();

    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    } else if (this.totalPages === 0) {
      this.currentPage = 1;
    }
  }

  async ImportCompanies() {
    const companiesToImport = this.mappedCompanies.map(company => {
      const { _selected, _errors, _isValid, _rowNumber, _nameMissing, _clientsMissing, _defaultClientMissing, _nameMatchWarning, _warnings, _mergedCompanyIds, ...cleanCompany } = company;

      // Construct year_end_date from month and day (format: yyyy-mm-dd)
      if (cleanCompany.year_end_month && cleanCompany.year_end_day) {
        const currentYear = new Date().getFullYear();
        cleanCompany.year_end_date = `${currentYear}-${cleanCompany.year_end_month}-${cleanCompany.year_end_day}`;
      }

      // Client Track imports only - '~'-joined legacy CompanyID(s) this company was created from
      // (see ProcessMappings) - lets the backend trace a Schedule/ContactLinks reference back to this
      // Fusion company later.
      if (_mergedCompanyIds && _mergedCompanyIds.length > 0) {
        cleanCompany.merged_ids = _mergedCompanyIds.join('~');
      }

      return { ...cleanCompany, _rowNumber };
    });

    const importErrors: { rowNumber: number; companyName: string; error: string }[] = [];
    const successfulImports: any[] = [];
    let importedCount = 0;

    // Keyed by pendingContact.guid (shared across every company row whose default contact name
    // matched, see ProcessMappings' pendingContactsByName) - so "John Smith" pending for 5 companies
    // only ever gets POSTed once here, and the other 4 just reuse the same real created contact.
    const createdPendingContacts = new Map<string, any>();

    await this.globalService.presentLoading(`Importing companies (0/${companiesToImport.length})...`, 3600000);

    for (const company of companiesToImport) {
      try {
        // Update loading message
        await this.globalService.presentLoading(`Importing companies (${importedCount + 1}/${companiesToImport.length})...`, 3600000);

        // Step 0: create the row's placeholder default contact for real, if it has one (see
        // ProcessMappings) - swaps the pending entry in company.clients/default_client_guid for the
        // real created contact before the company itself gets created below, so the rest of this
        // loop (which assigns company.clients via Step 6) never has to know the difference.
        const pendingContact: any = company.clients?.find((c: any) => c._pending_contact);
        if (pendingContact && createdPendingContacts.has(pendingContact.guid)) {
          // Already created for an earlier company row in this same import run - reuse it instead
          // of creating a duplicate contact.
          const createdContact = createdPendingContacts.get(pendingContact.guid);
          const idx = company.clients.indexOf(pendingContact);
          company.clients[idx] = { ...createdContact, role_guid: pendingContact.role_guid };
          if (company.default_client_guid === pendingContact.guid) company.default_client_guid = createdContact.guid;
        } else if (pendingContact) {
          const info = pendingContact._pending_contact_info;
          const clientPayload: any = {
            active: false,
            display_name: info.display_name,
            first_name: info.first_name,
            last_name: info.last_name,
            middle_name: info.middle_name,
            country_code: '+1',
            cell_country_code: '+1',
            alt_country_code: '+1',
            custom_id: info.custom_id,
            phone_number: info.phone_number,
            email: info.email,
            is_a_client: false,
            str_address: info.str_address,
            city: info.city,
            province: info.province,
            country: info.country,
            postal_code: info.postal_code,
            website: info.website,
            alt_phone: info.alt_phone,
            cell_phone: info.cell_phone,
            fax: info.fax,
            custom_data: info.custom_data,
            assigned_companies: [],
            family_members: [],
            team_members: [],
            groups: [],
            tags: [],
            gender: info.gender,
            birthday: info.birthday,
            anniversary: info.anniversary,
            sin: info.sin,
            spoken_language_arr: [],
            spoken_language: info.spoken_language,
            company_guid: '',
            role_guid: '0',
            all_team_members: true,
            year_end_month: '12',
            year_end_day: '31',
            year_end_date: `${new Date().getFullYear()}-12-31`,
          };

          let createdContact: any = null;
          try {
            const clientCreate: any = await this.apiService.post('clients', clientPayload);
            if (clientCreate.success) {
              createdContact = clientCreate.data[0];
              await this.apiService.put(`clients/${createdContact.guid}`, clientPayload);
              this.all_available_clients.push(createdContact);
            } else {
              importErrors.push({
                rowNumber: company._rowNumber,
                companyName: company.name || 'Unknown',
                error: `Failed to create default contact "${info.display_name}": ${clientCreate.message || 'Unknown error'}`
              });
            }
          } catch (contactError: any) {
            importErrors.push({
              rowNumber: company._rowNumber,
              companyName: company.name || 'Unknown',
              error: `Failed to create default contact "${info.display_name}": ${contactError.message || 'Unknown error'}`
            });
          }

          const idx = company.clients.indexOf(pendingContact);
          if (createdContact) {
            createdPendingContacts.set(pendingContact.guid, createdContact);
            // Swap the placeholder for the real thing - keeps company creation below going even
            // though the contact couldn't be created.
            company.clients[idx] = { ...createdContact, role_guid: pendingContact.role_guid };
            if (company.default_client_guid === pendingContact.guid) company.default_client_guid = createdContact.guid;
          } else {
            company.clients.splice(idx, 1);
            if (company.default_client_guid === pendingContact.guid) company.default_client_guid = '';
          }
        }

        // Step 1: Create the company
        const companyCreate: any = await this.apiService.post('companies', company);

        if (!companyCreate.success) {
          importErrors.push({
            rowNumber: company._rowNumber,
            companyName: company.name || 'Unknown',
            error: companyCreate.message || 'Failed to create company'
          });
          continue;
        }

        const companyGuid = companyCreate.data[0].guid;

        // Step 2: Update company details
        if (company.custom_data_arr && company.custom_data_arr.length > 0) {
          company.custom_data = JSON.stringify(company.custom_data_arr);
        }
        await this.apiService.put(`companies/${companyGuid}`, company);

        // Step 3: Assign tags if any
        if (company.tags && company.tags.length > 0) {
          for (const tag of company.tags) {
            await this.apiService.post(`companies/tags/${companyGuid}`, tag);
          }
        }

        // Step 4: Assign team members if any
        if (company.team_members && company.team_members.length > 0) {
          for (const teamMember of company.team_members) {
            await this.apiService.post(`companies/team_members/${companyGuid}`, teamMember);
          }
        }

        // Step 5: Assign groups if any
        if (company.groups && company.groups.length > 0) {
          for (const group of company.groups) {
            await this.apiService.post(`groups/companies/${companyGuid}`, group);
          }
        }

        // Step 5b: Assign bulk import groups to all imported companies
        for (const group of this.selectedGroups) {
          const alreadyAssigned = company.groups && company.groups.some((g: any) => g.guid === group.guid);
          if (!alreadyAssigned) {
            await this.apiService.post(`groups/companies/${companyGuid}`, group);
          }
        }

        // Step 5c: Assign bulk import tags to all imported companies
        for (const tag of this.selectedTags) {
          const alreadyAssigned = company.tags && company.tags.some((t: any) => t.guid === tag.guid);
          if (!alreadyAssigned) {
            await this.apiService.post(`companies/tags/${companyGuid}`, tag);
          }
        }

        // Step 6: Assign clients to company if any
        if (company.clients && company.clients.length > 0) {
          for (const client of company.clients) {
            const postData = {
              guid: client.guid,
              role_guid: client.role_guid || '0'
            };
            await this.apiService.post(`companies/clients/${companyGuid}`, postData);
          }
        }

        successfulImports.push(companyCreate.data[0]);
        importedCount++;

      } catch (error: any) {
        importErrors.push({
          rowNumber: company._rowNumber,
          companyName: company.name || 'Unknown',
          error: error.message || 'An unexpected error occurred'
        });
      }
    }

    this.globalService.dismissLoading();

    // Show summary message
    if (importErrors.length > 0) {
      const errorSummary = importErrors
        .map(e => `Row ${e.rowNumber} (${e.companyName}): ${e.error}`)
        .join('\n');

      const message = `Successfully imported ${successfulImports.length} company(ies).\n\nFailed to import ${importErrors.length} company(ies):\n${errorSummary}`;

      // Show an alert with the errors before dismissing
      await this.globalService.presentToastMessage(
        `Imported ${successfulImports.length} of ${companiesToImport.length} companies. ${importErrors.length} failed.`,
        successfulImports.length > 0
      );
    } else {
      await this.globalService.presentToastMessage(
        `Successfully imported ${successfulImports.length} company(ies).`,
        true
      );
    }

    await this.modalController.dismiss({
      refresh: true,
      imported: true,
      successCount: successfulImports.length
    });
  }

  // Contact assignment methods
  async presentCreateClientModal(company?: any) {
    const modal = await this.modalController.create({
      component: ClientCreatePage,
      cssClass: 'tall-modal medium-modal secondary-modal',
      backdropDismiss: false,
      componentProps: {
        simple_create: true,
        creating_for_company: !!company,
        selecting_for_company: !!company
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data && data.data.length > 0) {
        const newClient = data.data[0];
        this.all_available_clients.push(newClient);
        if (company) {
          const role_guid = newClient.role_guid && newClient.role_guid !== '0' ? newClient.role_guid : this.highestRoleGuid;
          let assignedClient = company.clients.find((c: any) => c.guid === newClient.guid);
          if (!assignedClient) {
            assignedClient = { ...newClient, role_guid };
            company.clients.push(assignedClient);
          }

          // Override whatever the previous default contact for the company was, if requested.
          if (newClient.is_default_for_company || !company.default_client_guid) {
            company.default_client_guid = assignedClient.guid;
          }

          // Override whoever was the default contact for this role, if requested.
          if (newClient.is_default_for_role) {
            company.clients.forEach((c: any) => {
              if (c.role_guid === role_guid) {
                c.is_default_for_role = c.guid === assignedClient.guid;
              }
            });
          }

          this.OnDataChanged();
        }
      }
    });
    return await modal.present();
  }

  async presentSelectClientsModal(company: any) {
    let companyClientsCopy = JSON.stringify(company.clients);
    const modal = await this.modalController.create({
      component: ContactsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        company: company,
        selecting_for_company: true,
        hide_companies: true,
        selected_clients: company.clients,
        remove_clients: company.clients,
        in_clients: [],
        target_header: 'Currently Selected'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data && data.data.clients) {
        company.clients = data.data.clients;
        // If the default was removed, clear it
        if (company.default_client_guid) {
          const stillAssigned = company.clients.some(c => c.guid === company.default_client_guid);
          if (!stillAssigned) {
            company.default_client_guid = '';
          }
        }
        // Auto-set default if only one client
        if (company.clients.length === 1) {
          company.default_client_guid = company.clients[0].guid;
        }
      } else {
        company.clients = JSON.parse(companyClientsCopy);
      }
      this.OnDataChanged();
    });
    return await modal.present();
  }

  async presentBulkSelectClientsModal() {
    let tempClients: any[] = [];
    const modal = await this.modalController.create({
      component: ContactsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        company: {},
        selecting_for_company: true,
        hide_companies: true,
        selected_clients: tempClients,
        remove_clients: [],
        in_clients: [],
        target_header: 'Bulk Selection'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data && data.data.clients && data.data.clients.length > 0) {
        this.ApplyBulkContacts(data.data.clients);
      }
    });
    return await modal.present();
  }

  ApplyBulkContacts(clients: any[]): void {
    const targetCompanies = this.GetSelectedCompaniesCount() > 0
      ? this.mappedCompanies.filter(c => c._selected)
      : this.mappedCompanies;

    targetCompanies.forEach(company => {
      clients.forEach(client => {
        const alreadyAssigned = company.clients.some(c => c.guid === client.guid);
        if (!alreadyAssigned) {
          company.clients.push({ ...client });
        }
      });
      // Auto-set default if no default and at least one client
      if (!company.default_client_guid && company.clients.length > 0) {
        company.default_client_guid = company.clients[0].guid;
      }
    });
    this.OnDataChanged();
  }

  RemoveClient(company: any, client: any): void {
    const idx = company.clients.findIndex(c => c.guid === client.guid);
    if (idx !== -1) {
      company.clients.splice(idx, 1);
    }
    if (company.default_client_guid === client.guid) {
      company.default_client_guid = company.clients.length > 0 ? company.clients[0].guid : '';
    }
    this.OnDataChanged();
  }

  ClearAllContacts(): void {
    const targetCompanies = this.GetSelectedCompaniesCount() > 0
      ? this.mappedCompanies.filter(c => c._selected)
      : this.mappedCompanies;

    targetCompanies.forEach(company => {
      company.clients = [];
      company.default_client_guid = '';
    });
    this.OnDataChanged();
  }

  // Companies (selected, or all if none selected) that have no default contact assigned yet and
  // have a usable name to build a default contact's display name from.
  GetCompaniesNeedingDefaultContact(): any[] {
    const targetCompanies = this.GetSelectedCompaniesCount() > 0
      ? this.mappedCompanies.filter(c => c._selected)
      : this.mappedCompanies;

    return targetCompanies.filter(c =>
      (!c.default_client_guid || c.default_client_guid.trim() === '') && c.name && c.name.trim() !== ''
    );
  }

  GetCompaniesWithoutDefaultContactCount(): number {
    return this.GetCompaniesNeedingDefaultContact().length;
  }

  // Warn before running - this creates real contacts in bulk and cannot be undone.
  async ConfirmQuickCreateDefaultContacts(): Promise<void> {
    const targetCompanies = this.GetCompaniesNeedingDefaultContact();
    if (targetCompanies.length === 0) return;

    const scopeText = this.GetSelectedCompaniesCount() > 0 ? 'selected companies' : 'companies';
    const alert = await this.alertController.create({
      header: 'Quickly Create Default Contacts',
      message: `This is a bulk action and cannot be undone. A row with a Default Contact Display Name mapped already gets its contact created automatically when you import - this only backfills the ${targetCompanies.length} ${scopeText} that still have no default contact at all (no name mapped, or mapping found no match). Each one gets a new contact named "Company Name - Default Contact" and set as that company's default contact with the highest permission level. It is recommended to manually find/assign default contacts before doing this.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Create Default Contacts',
          role: 'destructive',
          handler: () => this.QuickCreateDefaultContacts()
        }
      ]
    });
    await alert.present();
  }

  // Backfill for whatever's left after ProcessMappings()/ImportCompanies() - a company only ever
  // still has no default contact here if no Default Contact Display Name was mapped for its row, or
  // it was mapped but matched no existing contact by name AND for some reason never got its
  // placeholder created (e.g. this ran before import). Always falls back to the generic
  // "{Company Name} - Default Contact" since there's no parsed name left to use by this point.
  async QuickCreateDefaultContacts(): Promise<void> {
    const targetCompanies = this.GetCompaniesNeedingDefaultContact();
    if (targetCompanies.length === 0) return;

    let createdCount = 0;
    const creationErrors: { rowNumber: number; companyName: string; error: string }[] = [];

    await this.globalService.presentLoading(`Creating default contacts (0/${targetCompanies.length})...`, 3600000);

    for (const company of targetCompanies) {
      await this.globalService.presentLoading(`Creating default contacts (${createdCount + creationErrors.length + 1}/${targetCompanies.length})...`, 3600000);
      try {
        const clientPayload: any = {
          active: false,
          display_name: `${company.name} - Default Contact`,
          first_name: 'Default',
          last_name: 'Contact',
          middle_name: '',
          country_code: '+1',
          cell_country_code: '+1',
          alt_country_code: '+1',
          custom_id: '',
          phone_number: '',
          email: '',
          is_a_client: false,
          str_address: '',
          city: '',
          province: '',
          country: '',
          postal_code: '',
          website: '',
          alt_phone: '',
          cell_phone: '',
          fax: '',
          custom_data: '',
          assigned_companies: [],
          family_members: [],
          team_members: [],
          groups: [],
          tags: [],
          gender: '',
          birthday: '',
          anniversary: '',
          sin: '',
          spoken_language_arr: [],
          spoken_language: '',
          company_guid: '',
          role_guid: '0',
          all_team_members: true,
          year_end_month: '12',
          year_end_day: '31',
          year_end_date: `${new Date().getFullYear()}-12-31`,
        };

        const clientCreate: any = await this.apiService.post('clients', clientPayload);
        if (!clientCreate.success) {
          creationErrors.push({
            rowNumber: company._rowNumber,
            companyName: company.name,
            error: clientCreate.message || 'Failed to create default contact'
          });
          continue;
        }

        const newClient = clientCreate.data[0];
        await this.apiService.put(`clients/${newClient.guid}`, clientPayload);

        const roleGuid = this.highestRoleGuid;
        const assignedClient = { ...newClient, role_guid: roleGuid };
        const alreadyAssigned = company.clients.some((c: any) => c.guid === assignedClient.guid);
        if (!alreadyAssigned) company.clients.push(assignedClient);
        company.default_client_guid = assignedClient.guid;

        this.all_available_clients.push(newClient);
        createdCount++;
      } catch (error: any) {
        creationErrors.push({
          rowNumber: company._rowNumber,
          companyName: company.name || 'Unknown',
          error: error.message || 'An unexpected error occurred'
        });
      }
    }

    this.globalService.dismissLoading();
    this.OnDataChanged();

    if (creationErrors.length > 0) {
      this.globalService.presentToastMessage(
        `Created ${createdCount} of ${targetCompanies.length} default contact(s). ${creationErrors.length} failed.`,
        createdCount > 0
      );
    } else {
      this.globalService.presentToastMessage(`Created ${createdCount} default contact(s).`, true);
    }
  }

  GetClientDisplayName(company: any, guid: string): string {
    const client = company.clients.find(c => c.guid === guid);
    return client ? client.display_name : '';
  }

  // Team member assignment methods
  async presentSelectTeamMembersModal(company: any) {
    let companyTeamMembersCopy = JSON.stringify(company.team_members);
    const modal = await this.modalController.create({
      component: TeamMembersSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        company: company,
        selecting_for_company: true,
        selected_team_members: company.team_members,
        remove_team_members: company.team_members,
        in_team_members: [],
        target_header: 'Currently Selected'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        company.team_members = data.data;
      } else {
        company.team_members = JSON.parse(companyTeamMembersCopy);
      }
      this.OnDataChanged();
    });
    return await modal.present();
  }

  RemoveTeamMember(company: any, teamMember: any): void {
    const idx = company.team_members.findIndex(tm => tm.guid === teamMember.guid);
    if (idx !== -1) {
      company.team_members.splice(idx, 1);
    }
    this.OnDataChanged();
  }

  //Returns the text value of the employee type
  GetEmployeeTypeText(guid) {
    let i = this.globalService.GetArrayIndex(this.team_member_roles, guid);
    if (i == -1)
      return "No Role";
    else
      return this.team_member_roles[i].title;
  }
  //Returns the colour of the employee tpe
  GetEmployeeTypeColour(guid) {
    let i = this.globalService.GetArrayIndex(this.team_member_roles, guid);
    if (i == -1)
      return "light-blue";
    else
      return this.team_member_roles[i].colour;
  }



}
